import React, { useState, useEffect, useRef } from 'react';
import { FileText, CheckCircle2, AlertCircle, CreditCard, Shield, XCircle, RefreshCw } from 'lucide-react';

export default function TermsOfService() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  return (
    <div className="bg-white text-zinc-900">
      {/* Hero Section */}
      <section className="border-b border-zinc-200" data-animate="hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`text-center transition-all duration-1000 ease-out ${
            visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-blue-600 text-white text-xs font-medium tracking-wider mb-8 rounded">
              <FileText size={14} />
              TERMS OF SERVICE
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Terms of Service
            </h1>
            <p className="text-lg text-zinc-600 mb-4">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto leading-relaxed">
              Please read these Terms of Service carefully before using the Digital Trent platform. By accessing or using our Service, you agree to be bound by these terms.
            </p>
          </div>
        </div>
      </section>

      {/* Agreement to Terms */}
      <section className="border-b border-zinc-200" data-animate="agreement">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('agreement') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Agreement to Terms</h2>
            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>
                These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and Digital Trent ("Digital Trent", "we", "us", or "our") governing your access to and use of the Digital Trent platform, including our website, applications, and services (collectively, the "Service").
              </p>
              <p>
                By creating an account, accessing, or using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not access or use the Service.
              </p>
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg mt-4">
                <p className="font-medium text-zinc-900 mb-2">Important Notice:</p>
                <p className="text-sm">
                  These Terms are governed by the laws of England and Wales. Our Service is designed for business use and is intended for users aged 18 and over. You represent and warrant that you have the legal capacity to enter into these Terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Description */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="service">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('service') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Description of Service</h2>
            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>
                Digital Trent is an AI-powered marketing and CRM platform that provides:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <span>AI-generated marketing strategies and business planning tools</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <span>Automated content creation for multiple marketing channels</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <span>Customer relationship management (CRM) functionality</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <span>Campaign management and scheduling tools</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <span>Performance analytics and reporting</span>
                </li>
              </ul>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg mt-6">
                <p className="font-medium text-zinc-900 mb-2">Service Availability:</p>
                <p>
                  We strive to provide uninterrupted access to the Service but do not guarantee that the Service will be available at all times. We reserve the right to modify, suspend, or discontinue any part of the Service with or without notice. We are not liable for any modification, suspension, or discontinuation of the Service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Account Registration */}
      <section className="border-b border-zinc-200" data-animate="account">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('account') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Account Registration and Security</h2>
            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Registration Requirements</h3>
                <p className="mb-3">To access the Service, you must create an account by providing:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Accurate, current, and complete information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>A valid email address and contact information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Information about your business and marketing needs</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  You agree to update your information promptly if it changes. Providing false or misleading information may result in account termination.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Account Security</h3>
                <p className="mb-3">You are responsible for:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Maintaining the confidentiality of your account credentials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>All activities that occur under your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Notifying us immediately of any unauthorised use or security breach</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  We are not liable for any loss or damage arising from your failure to protect your account credentials.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Account Usage</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Accounts are for individual or business use only and may not be shared</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You may not create multiple accounts to circumvent platform limitations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You must be 18 years or older to create an account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Business accounts must be authorised by the business entity</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription and Billing */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="billing">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('billing') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <CreditCard size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Subscription Plans and Billing</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Free Trial</h3>
                <p>
                  We offer a 14-day free trial for new users. No credit card is required to start your trial. At the end of the trial period, you may choose to subscribe to a paid plan or your account will revert to limited functionality.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Subscription Plans</h3>
                <p className="mb-3">We offer multiple subscription tiers with different features and pricing:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>DIY Starter:</strong> Self-service plan with AI tools and basic features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Assisted Growth:</strong> Includes strategy review and implementation support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Fully Managed VIP:</strong> Comprehensive managed service with dedicated support</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  Current pricing and plan details are available on our pricing page at <a href="https://digitaltrent.com/pricing" className="text-blue-600 hover:underline">digitaltrent.com/pricing</a>.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Billing Terms</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually based on your selection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Auto-Renewal:</strong> Subscriptions automatically renew at the end of each billing period unless cancelled</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Payment Method:</strong> You must provide a valid payment method and authorise us to charge it</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Failed Payments:</strong> If payment fails, we may suspend your account until payment is resolved</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Taxes:</strong> Fees are exclusive of taxes; you are responsible for applicable taxes</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Price Changes</h3>
                <p>
                  We reserve the right to change our pricing at any time. Price changes will take effect at the start of the next billing cycle after at least 30 days' notice. If you do not agree to the price change, you may cancel your subscription before the new price takes effect.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Refund Policy</h3>
                <p className="mb-3">Under the Consumer Rights Act 2015 and Consumer Contracts Regulations 2013:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>14-Day Cooling Off Period:</strong> You have the right to cancel within 14 days of purchase for a full refund</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>After 14 Days:</strong> Fees are generally non-refundable except where required by law</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Service Issues:</strong> If the Service is not as described or has defects, you may be entitled to a refund</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Cancellation:</strong> Cancelled subscriptions continue until the end of the current billing period without refund</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  To request a refund, contact us at <a href="mailto:billing@digitaltrent.co.uk" className="text-blue-600 hover:underline">billing@digitaltrent.co.uk</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Acceptable Use */}
      <section className="border-b border-zinc-200" data-animate="acceptable">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('acceptable') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <Shield size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Acceptable Use Policy</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>

              <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3 flex items-center gap-2">
                  <XCircle size={20} className="text-red-600" />
                  Prohibited Activities
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Violate any applicable laws, regulations, or third-party rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Use the Service to create, distribute, or promote illegal, harmful, or offensive content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Engage in spamming, phishing, or unsolicited mass communications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Attempt to gain unauthorised access to the Service or other users' accounts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Interfere with or disrupt the Service, servers, or networks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Use automated systems (bots, scrapers) without authorisation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Reverse engineer, decompile, or attempt to extract source code</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Resell or redistribute the Service without authorisation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Remove or alter any proprietary notices or labels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>Impersonate any person or entity or misrepresent your affiliation</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Content Standards</h3>
                <p className="mb-2">Content you create using our Service must comply with applicable laws and must not:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Infringe intellectual property rights or violate privacy rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Contain false, misleading, or deceptive information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Promote violence, discrimination, or hatred</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Violate advertising standards or consumer protection laws</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-2">Enforcement</h3>
                <p>
                  We reserve the right to investigate violations of these Terms and take appropriate action, including suspending or terminating accounts, removing content, and reporting illegal activity to law enforcement. We may suspend or terminate your account immediately without notice if we believe you have violated these Terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intellectual Property */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="ip">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('ip') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Intellectual Property Rights</h2>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Our Intellectual Property</h3>
                <p className="mb-3">
                  The Service, including its software, design, text, graphics, logos, and other content (excluding user-generated content), is owned by Digital Trent and protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p>
                  We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Service for your internal business purposes in accordance with these Terms. This licence does not include the right to copy, modify, distribute, sell, or lease any part of the Service.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Your Content</h3>
                <p className="mb-3">
                  You retain ownership of content you upload, create, or submit through the Service ("Your Content"). By using the Service, you grant us a worldwide, non-exclusive, royalty-free licence to:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Store, process, and display Your Content to provide the Service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Use Your Content to improve our AI algorithms and Service quality</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Create backups and ensure Service reliability</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  This licence terminates when you delete Your Content or close your account, except for content that has been shared or distributed through the Service.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">AI-Generated Content</h3>
                <p className="mb-3">
                  Content generated by our AI tools based on your inputs is provided to you under these Terms. You may use this content for your business purposes, subject to the following conditions:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You are responsible for reviewing and ensuring AI-generated content is accurate and appropriate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You must comply with applicable laws when using AI-generated content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>AI-generated content may not be unique; similar content may be generated for other users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>We do not claim ownership of AI-generated content created specifically for you</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Trademarks</h3>
                <p>
                  "Digital Trent" and our logo are trademarks of Digital Trent. You may not use our trademarks without our prior written permission. Other trademarks, service marks, and logos used on the Service are the property of their respective owners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Limitation of Liability */}
      <section className="border-b border-zinc-200" data-animate="liability">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('liability') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <AlertCircle size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Disclaimers and Limitation of Liability</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Service Warranty</h3>
                <p className="mb-3">
                  Under the Consumer Rights Act 2015, we are required to provide the Service with reasonable care and skill, as described, and fit for purpose. However, to the extent permitted by law:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>The Service is provided "as is" without warranties beyond those required by law</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>We do not guarantee uninterrupted, error-free, or secure operation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>AI-generated content may contain errors or inaccuracies; you are responsible for reviewing it</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>We do not guarantee specific marketing results or business outcomes</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-blue-600 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Limitation of Liability</h3>
                <p className="mb-3">
                  Nothing in these Terms excludes or limits our liability for:
                </p>
                <ul className="space-y-2 ml-4 mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                    <span>Death or personal injury caused by negligence</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                    <span>Fraud or fraudulent misrepresentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                    <span>Any other liability that cannot be excluded under UK law</span>
                  </li>
                </ul>

                <p className="mb-3">Subject to the above, and to the maximum extent permitted by law:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Our total liability for any claims arising from these Terms is limited to the amount you paid us in the 12 months before the claim arose</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>We are not liable for indirect, consequential, special, or punitive damages, including lost profits, data loss, or business interruption</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>We are not liable for losses resulting from your use of AI-generated content or business decisions based on our Service</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Your Responsibility</h3>
                <p>
                  You acknowledge that the Service provides tools and recommendations, but ultimate responsibility for your marketing strategies, content, and business decisions rests with you. You should review all AI-generated content before use and ensure compliance with applicable laws and regulations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Termination */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="termination">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('termination') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <RefreshCw size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Cancellation and Termination</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Your Right to Cancel</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You may cancel your subscription at any time through your account settings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Cancellation takes effect at the end of your current billing period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You will retain access to the Service until the end of the paid period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>No refunds are provided for partial billing periods (except as required by law)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Our Right to Terminate</h3>
                <p className="mb-3">We may suspend or terminate your account if:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You violate these Terms or our Acceptable Use Policy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Your payment fails or your account is past due</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Your use of the Service poses security or legal risks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>We cease providing the Service (with reasonable notice)</span>
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  We will provide notice where reasonably possible, but may terminate immediately in cases of serious violations.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Effect of Termination</h3>
                <p className="mb-3">Upon termination or cancellation:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Your right to access and use the Service immediately ceases</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Your data will be retained for 90 days for recovery, then permanently deleted</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>You may request a data export before termination takes effect</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Provisions regarding intellectual property, liability, and disputes survive termination</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Governing Law */}
      <section className="border-b border-zinc-200" data-animate="law">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('law') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Governing Law and Dispute Resolution</h2>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Governing Law</h3>
                <p>
                  These Terms are governed by and construed in accordance with the laws of England and Wales. The parties submit to the exclusive jurisdiction of the courts of England and Wales for any disputes arising from these Terms.
                </p>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Dispute Resolution</h3>
                <p className="mb-3">
                  If you have a concern or dispute, please contact us first at <a href="mailto:support@digitaltrent.co.uk" className="text-blue-600 hover:underline">support@digitaltrent.co.uk</a>. We will attempt to resolve disputes informally.
                </p>
                <p>
                  If we cannot resolve the dispute informally, you may pursue resolution through UK courts or, where applicable, use alternative dispute resolution services such as the UK Online Dispute Resolution platform for online purchases.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-zinc-900 mb-3">Consumer Rights</h3>
                <p>
                  Nothing in these Terms affects your statutory rights as a consumer under UK law, including rights under the Consumer Rights Act 2015 and Consumer Contracts Regulations 2013.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* General Provisions */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="general">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('general') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">General Provisions</h2>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Changes to Terms</h3>
                  <p className="text-sm">We may update these Terms. Material changes will be notified 30 days in advance. Continued use constitutes acceptance.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Assignment</h3>
                  <p className="text-sm">You may not assign these Terms. We may assign our rights and obligations to a successor entity.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Severability</h3>
                  <p className="text-sm">If any provision is invalid, the remaining provisions remain in full effect.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Entire Agreement</h3>
                  <p className="text-sm">These Terms and our Privacy Policy constitute the entire agreement between you and Digital Trent.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Waiver</h3>
                  <p className="text-sm">Failure to enforce any provision does not constitute a waiver of that provision.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Force Majeure</h3>
                  <p className="text-sm">We are not liable for delays or failures due to circumstances beyond our reasonable control.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="border-b border-zinc-200" data-animate="contact">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Contact Information</h2>

            <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
              <p className="text-zinc-600 mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2 text-zinc-900">
                <p><strong>Email:</strong> <a href="mailto:support@digitaltrent.co.uk" className="text-blue-600 hover:underline">support@digitaltrent.co.uk</a></p>
                <p><strong>Billing Enquiries:</strong> <a href="mailto:billing@digitaltrent.co.uk" className="text-blue-600 hover:underline">billing@digitaltrent.co.uk</a></p>
                <p><strong>Location:</strong> Nottingham, United Kingdom</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Acknowledgment */}
      <section data-animate="acknowledgment">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`bg-white border-2 border-blue-600 p-8 rounded-lg text-center transition-all duration-1000 ease-out ${
            visibleSections.has('acknowledgment') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <CheckCircle2 size={48} className="text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-zinc-900 mb-3">Acknowledgment</h3>
            <p className="text-zinc-600 leading-relaxed">
              By using the Digital Trent Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
